const STATIC_WIDTH = '(max-width: 979px)';
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/* The film's own keyframes run the clock at display rate; on mobile that is 60
   style recalcs a second across the live act, which iPhone Safari kills the
   page for. V3 survives because its engine only writes changed values, so
   mobile drives the same clock from JS at 24fps and writes only when the
   quantised value moves — same film, less than half the recalc. */
const MOBILE_FPS = 24;
const TICK_MS = 150;
const DURATION_S = 34.5;

/* The acts' wrappers, addressed by their distinctive inline styles so the
   stored stage markup never changes. `world` is the whole 1040x684 film box:
   act 3 detaches it, so the phone beat costs no live DOM at all. */
const ACT_PARTS = {
  world: "[style*='width:1040px;height:684px']",
  sam: "[style*='--fq:']",
  maya: "[style*='z-index:7;left:0;top:88px']",
  cue: "[style*='--ps:']",
  phone: "[style*='1.45142']",
} as const;

type PartName = keyof typeof ACT_PARTS;

/* Which parts are absent in each act state. display:none was not enough — a
   hidden subtree keeps its computed style, so the inherited clock still
   re-resolved every element each frame; these are detached instead. Act 3 drops
   the whole world and the CSS poster stands in for the phone. */
const ABSENT: Record<string, readonly PartName[]> = {
  '1': ['maya', 'phone'],
  '12': ['phone'],
  '2': ['sam', 'phone'],
  '23': ['sam', 'cue', 'phone'],
  '3': ['world'],
};

/* Re-attachment order, one subtree per tick: putting the whole world back in a
   single tick was one huge layout, which reloaded the page at every loop wrap. */
const ATTACH_ORDER: readonly PartName[] = ['world', 'sam', 'maya', 'cue', 'phone'];

function mountClock(root: HTMLElement): void {
  const visual = root.querySelector<HTMLElement>('[data-v4-visual]');
  root.style.animation = 'none';

  let inView = true;
  if (visual && 'IntersectionObserver' in window) {
    new IntersectionObserver(
      (entries) => {
        inView = entries.some((e) => e.isIntersecting);
      },
      { threshold: 0 },
    ).observe(visual);
  }

  const step = 1 / MOBILE_FPS;
  let elapsed = 0;
  let lastTs = 0;
  let lastWritten = '';
  const tick = (ts: number) => {
    requestAnimationFrame(tick);
    // Clamp the delta so a throttled or backgrounded frame cannot jump the film.
    const dt = lastTs ? Math.min(ts - lastTs, 100) : 0;
    lastTs = ts;
    if (!inView || document.hidden) {
      return;
    }
    elapsed = (elapsed + dt / 1000) % DURATION_S;
    const quantised = (Math.round(elapsed / step) * step).toFixed(3);
    if (quantised !== lastWritten) {
      lastWritten = quantised;
      root.style.setProperty('--v4T', quantised);
    }
  };
  requestAnimationFrame(tick);
}

/* The gates lead the film's own fades by >=0.3s each side, so nothing visible
   is ever cut; the tick only ever flips acts inside those margins. */
function mountActGater(root: HTMLElement): void {
  const parts = new Map<PartName, { node: Element; anchor: Comment }>();
  for (const name of Object.keys(ACT_PARTS) as PartName[]) {
    const node = root.querySelector(ACT_PARTS[name]);
    if (node?.parentNode) {
      const anchor = document.createComment(`v4:${name}`);
      node.parentNode.insertBefore(anchor, node);
      parts.set(name, { node, anchor });
    }
  }

  let current = '';
  const gate = () => {
    const wide = !window.matchMedia(STATIC_WIDTH).matches;
    const T = parseFloat(getComputedStyle(root).getPropertyValue('--v4T')) || 0;
    const act = wide
      ? 'wide'
      : T < 19.2
        ? '1'
        : T < 20.5
          ? '12'
          : T < 23.4
            ? '2'
            : T < 24.6
              ? '23'
              : '3';
    if (act === current) {
      return;
    }
    current = act;
    if (wide) {
      delete root.dataset.v4Act;
    } else {
      root.dataset.v4Act = act;
    }
  };

  const apply = () => {
    const absent = ABSENT[current] ?? [];
    for (const [name, part] of parts) {
      if (absent.includes(name) && part.node.isConnected) {
        part.node.remove();
      }
    }
    for (const name of ATTACH_ORDER) {
      const part = parts.get(name);
      if (part && !absent.includes(name) && !part.node.isConnected) {
        part.anchor.parentNode?.insertBefore(part.node, part.anchor.nextSibling);
        return;
      }
    }
  };

  window.setInterval(() => {
    gate();
    apply();
  }, TICK_MS);
  gate();
  apply();
}

/*
 * Mobile-only companion to the V4 film. The stacked widths run the same live
 * stage the desktop does, but on a slower hand-driven clock and with only the
 * act on screen attached to the document — the two things that let iPhone
 * Safari survive it. Nothing here runs at desktop widths, where the film stays
 * exactly as authored: CSS keyframes, full stage, display rate.
 */
export function mountV4MobileStage(root: HTMLElement): void {
  if (!window.matchMedia(STATIC_WIDTH).matches || window.matchMedia(REDUCED_MOTION).matches) {
    return;
  }
  mountClock(root);
  mountActGater(root);
}
